export const AUTH_IDENTITY_STORAGE_KEY = "vaultn.auth.auth0Subject";

export enum AuthIdentityConsistencyStatus {
  ChangedIdentity = "changed_identity",
  InvalidIdentity = "invalid_identity",
  NewIdentity = "new_identity",
  SameIdentity = "same_identity",
  Unauthenticated = "unauthenticated",
}

interface AuthIdentityConsistencyOptions {
  auth0Subject?: unknown;
  isAuthenticated: boolean;
  storage?: Storage;
}

export interface AuthIdentityConsistencyResult {
  currentAuth0Subject: string | null;
  previousAuth0Subject: string | null;
  shouldClearLocalState: boolean;
  status: AuthIdentityConsistencyStatus;
}

function getAuthIdentityStorage(storage?: Storage): Storage | null {
  if (storage) {
    return storage;
  }

  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

function getValidAuth0Subject(auth0Subject: unknown): string | null {
  if (typeof auth0Subject !== "string") {
    return null;
  }

  const trimmedAuth0Subject = auth0Subject.trim();

  return trimmedAuth0Subject || null;
}

function readStoredAuth0Subject(storage: Storage | null): string | null {
  try {
    return storage?.getItem(AUTH_IDENTITY_STORAGE_KEY) ?? null;
  } catch {
    return null;
  }
}

function storeAuth0Subject(storage: Storage | null, auth0Subject: string): void {
  try {
    storage?.setItem(AUTH_IDENTITY_STORAGE_KEY, auth0Subject);
  } catch {
    // Identity persistence is non-critical; auth state can still be rebuilt from Auth0.
  }
}

function removeStoredAuth0Subject(storage: Storage | null): void {
  try {
    storage?.removeItem(AUTH_IDENTITY_STORAGE_KEY);
  } catch {
    // Identity persistence is non-critical; auth state can still be rebuilt from Auth0.
  }
}

export function clearStoredAuth0Subject(storage?: Storage): void {
  removeStoredAuth0Subject(getAuthIdentityStorage(storage));
}

export function getStoredAuth0Subject(storage?: Storage): string | null {
  return readStoredAuth0Subject(getAuthIdentityStorage(storage));
}

export function syncAuthIdentity(options: AuthIdentityConsistencyOptions): AuthIdentityConsistencyResult {
  const storage = getAuthIdentityStorage(options.storage);
  const previousAuth0Subject = readStoredAuth0Subject(storage);

  if (!options.isAuthenticated) {
    removeStoredAuth0Subject(storage);

    return {
      currentAuth0Subject: null,
      previousAuth0Subject,
      shouldClearLocalState: false,
      status: AuthIdentityConsistencyStatus.Unauthenticated,
    };
  }

  const currentAuth0Subject = getValidAuth0Subject(options.auth0Subject);

  if (!currentAuth0Subject) {
    return {
      currentAuth0Subject: null,
      previousAuth0Subject,
      shouldClearLocalState: false,
      status: AuthIdentityConsistencyStatus.InvalidIdentity,
    };
  }

  storeAuth0Subject(storage, currentAuth0Subject);

  if (!previousAuth0Subject) {
    return {
      currentAuth0Subject,
      previousAuth0Subject: null,
      shouldClearLocalState: false,
      status: AuthIdentityConsistencyStatus.NewIdentity,
    };
  }

  const shouldClearLocalState = previousAuth0Subject !== currentAuth0Subject;

  return {
    currentAuth0Subject,
    previousAuth0Subject,
    shouldClearLocalState,
    status: shouldClearLocalState
      ? AuthIdentityConsistencyStatus.ChangedIdentity
      : AuthIdentityConsistencyStatus.SameIdentity,
  };
}
